// Topic keys and query → topic inference for contact routing.
export const TOPIC_KEYS = [
  "deal_packet",
  "dealer_paperwork",
  "order_entry",
  "cash_contracts",
  "commission_questions",
  "crm_access",
  "crm_workflows",
  "delivery_scheduling",
  "delivery_reschedule",
  "service_scheduling",
  "site_access_requirements",
  "repair_requests",
  "warranty_claims",
  "marketing_materials",
  "creative_assets",
  "facebook_ads",
  "google_ads",
  "website_updates",
  "steel_quotes",
  "steel_vendors",
  "rto_contracts",
  "device_setup",
  "funding_docs",
  "inventory_requests",
  "quality_control",
  "shed_manufacturing",
  "signage_requests",
] as const;

export function inferTopicKey(q: string): string | null {
  const s = (q || "").toLowerCase().trim();

  if (/(sales\s+paperwork|dealer\s+paperwork|deal\s+submission|submit\s+(a\s+)?deal|submit\s+paperwork|paperwork\s+for\s+a\s+deal)/i.test(s)) {
    return "dealer_paperwork";
  }
  if (/(deal\s+packet)/i.test(s)) return "deal_packet";
  if (/(order\s+entry|enter\s+the\s+order|input\s+the\s+order)/i.test(s)) return "order_entry";

  if (/(cash\s+contract|cash\s+deal)/i.test(s)) return "cash_contracts";
  if (/(funding\s+docs|funding\s+documents|lender\s+docs)/i.test(s)) return "funding_docs";

  if (/(commission|spiff|pay\s*plan)/i.test(s)) return "commission_questions";

  if (/(can('?|no)t\s+log\s*in|login\s+issue|can('?|no)t\s+access|permissions?|user\s+access)/i.test(s)) return "crm_access";
  if (/(workflow|automation|pipeline|trigger)/i.test(s)) return "crm_workflows";

  if (/(delivery\s+date|schedule\s+delivery|set\s+up\s+delivery)/i.test(s)) return "delivery_scheduling";
  if (/(reschedule|move\s+delivery|change\s+delivery)/i.test(s)) return "delivery_reschedule";
  if (/(service\s+schedul|schedule\s+service)/i.test(s)) return "service_scheduling";
  if (/(site\s+access|access\s+requirements|gate\s+code|clearance)/i.test(s)) return "site_access_requirements";

  if (/(warranty|claim)/i.test(s)) return "warranty_claims";
  if (/(repair|service\s+request)/i.test(s)) return "repair_requests";

  if (/(creative\s+asset|graphic|design|flyer|brochure)/i.test(s)) return "creative_assets";
  if (/(facebook\s+ad|meta\s+ad)/i.test(s)) return "facebook_ads";
  if (/(google\s+ad|search\s+ad)/i.test(s)) return "google_ads";
  if (/(website|site\s+update|update\s+the\s+site)/i.test(s)) return "website_updates";
  if (/(marketing\s+material|signage|yard\s+sign|banner)/i.test(s)) return "marketing_materials";
  if (/(signage|sign\s+request)/i.test(s)) return "signage_requests";

  if (/(steel\s+quote|metal\s+building\s+quote|steel\s+building)/i.test(s)) return "steel_quotes";
  if (/(steel\s+vendor|vendor\s+for\s+steel)/i.test(s)) return "steel_vendors";

  if (/(rto|rent[\s-]?to[\s-]?own|lease\s+to\s+own)/i.test(s)) return "rto_contracts";
  if (/(tablet|device\s+setup|set\s+up\s+my\s+device)/i.test(s)) return "device_setup";

  if (/(quality\s+control|qc\s+issue)/i.test(s)) return "quality_control";
  if (/(manufacturing|plant|shed\s+build|production)/i.test(s)) return "shed_manufacturing";

  const direct = (TOPIC_KEYS as readonly string[]).find((k) => s.includes(k.replace(/_/g, " ")));
  return direct ?? null;
}

export function isLocationLookupIntent(q: string): boolean {
  const s = (q || "").toLowerCase();

  const contactWords = [
    "number",
    "phone",
    "call",
    "manager",
    "address",
    "located",
    "where is",
    "working at",
    "who works at",
    "contact",
    "contact information",
    "email",
    "hours",
  ];
  const hasContactIntent = contactWords.some((w) => s.includes(w));

  const hasLocationWord = s.includes("location") || s.includes("lot") || s.includes("office");

  const knownLocationNames = ["boiling springs", "conway", "moncks corner", "cottageville"];
  const mentionsKnownLocation = knownLocationNames.some((loc) => s.includes(loc));

  if (mentionsKnownLocation && hasContactIntent) return true;
  if (hasLocationWord && hasContactIntent) return true;

  return false;
}
